import { useEffect, useRef, useState } from "react";

// Hook to determine if the text content of an element is truncated (i.e., overflows its container)
// returns a ref to be attached to the element and a boolean indicating if it's truncated
//
// `content` is optional and only used as an effect dependency: pass the text/node currently
// rendered inside the ref'd element so the check re-runs when it changes. This matters for
// long-lived elements (e.g. a window title) whose content can change without the element
// itself resizing — a ResizeObserver alone won't catch that case.
export const useIsTruncated = <T extends HTMLElement = HTMLDivElement>(content?: unknown) => {
    const ref = useRef<T>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
        const element = ref.current;
        if (!element) {
            setIsTruncated(false);
            return;
        }

        let retryCount = 0;
        const MAX_RETRIES = 10;

        const checkTruncation = () => {
            // Wait for element to have dimensions
            if (element.clientWidth === 0 || element.scrollWidth === 0) {
                if (retryCount < MAX_RETRIES) {
                    retryCount++;
                    setTimeout(checkTruncation, 50);
                }
                return;
            }

            const range = document.createRange();
            range.selectNodeContents(element);
            const textWidth = range.getBoundingClientRect().width;
            range.detach();
            const containerWidth = element.getBoundingClientRect().width;
            setIsTruncated(textWidth > containerWidth);
        };

        // Initial check
        setTimeout(checkTruncation, 100);

        // Set up resize observer
        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== "undefined") {
            resizeObserver = new ResizeObserver(() => {
                setTimeout(checkTruncation, 10);
            });
            resizeObserver.observe(element);
        } else {
            // Fallback for older browsers
            const handleResize = () => setTimeout(checkTruncation, 50);
            window.addEventListener("resize", handleResize);
            return () => window.removeEventListener("resize", handleResize);
        }

        return () => resizeObserver?.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content]);

    return { ref, isTruncated };
};
